import React, { useState, useEffect, useCallback } from 'react';
import { apiClient } from '../../api/client';
import { CreateFamilyGroupModal } from './CreateFamilyGroupModal';

interface Guest {
  id: string;
  fullName: string;
  ticketNumber?: string | null;
  chapterAffiliation?: string | null;
  familyAssociation?: string | null;
  sponsorshipLevel?: string;
  accessibilityNeeds?: string | null;
  dietaryPreference?: string | null;
  protocolVip: boolean;
  isChapterMember?: boolean;
  rsvpStatus?: string;
  paymentStatus?: string;
  seat?: Array<{
    seatNumber: number;
    status: string;
    table: { tableNumber: number; tableName?: string; zone?: { name: string } };
  }>;
  registration?: { id: string; ticket?: { tierName: string; ticketNumber: number; orderId?: string; order?: { referenceId: string } } | null } | null;
  orderGuestCount?: number | null;
}

interface GuestsResponse {
  guests: Guest[];
  page: number;
  totalPages: number;
  totalGuests: number;
}

const ACCESSIBILITY_OPTIONS = [
  'Wheelchair access',
  'Walker/mobility aid',
  'Visual impairment',
  'Hearing impairment',
  'Service animal',
  'Ground floor seating',
  'Near restroom',
  'Dietary/allergy (medical)',
  'Companion assistance required',
];

const styles: Record<string, React.CSSProperties> = {
  container: { padding: '0' },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '1rem',
  },
  title: {
    fontSize: '1.1rem',
    fontWeight: 700,
    color: '#1a6b3c',
    margin: 0,
  },
  searchRow: {
    display: 'flex',
    gap: '0.75rem',
    marginBottom: '1rem',
    alignItems: 'center',
  },
  searchInput: {
    flex: 1,
    padding: '0.6rem 0.85rem',
    border: '1px solid #ccc',
    borderRadius: '6px',
    fontSize: '0.85rem',
    maxWidth: '420px',
  },
  countBadge: {
    fontSize: '0.8rem',
    color: '#666',
    fontWeight: 500,
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse' as const,
    fontSize: '0.85rem',
  },
  th: {
    textAlign: 'left' as const,
    padding: '0.6rem 0.75rem',
    borderBottom: '2px solid #e0e0e0',
    color: '#333',
    fontWeight: 600,
    fontSize: '0.8rem',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.3px',
  },
  td: {
    padding: '0.6rem 0.75rem',
    borderBottom: '1px solid #f0f0f0',
    color: '#333',
  },
  row: {
    cursor: 'pointer',
    transition: 'background 0.15s',
  },
  rowSelected: {
    cursor: 'pointer',
    backgroundColor: '#e8f5e9',
  },
  vipBadge: {
    display: 'inline-block',
    fontSize: '0.7rem',
    fontWeight: 700,
    padding: '2px 6px',
    borderRadius: '3px',
    backgroundColor: '#fff3e0',
    color: '#e65100',
  },
  chapterMemberBadge: {
    display: 'inline-block',
    fontSize: '0.7rem',
    fontWeight: 700,
    padding: '2px 6px',
    borderRadius: '3px',
    backgroundColor: '#e8f5e9',
    color: '#2e7d32',
    marginLeft: '4px',
  },
  editPanel: {
    position: 'fixed' as const,
    inset: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  modalOverlay: {
    position: 'fixed' as const,
    inset: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  modalContent: {
    backgroundColor: '#fff',
    borderRadius: '12px',
    padding: '1.75rem',
    width: '780px',
    maxWidth: '95vw',
    maxHeight: '85vh',
    overflowY: 'auto' as const,
  },
  editHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '1rem',
  },
  editTitle: {
    fontSize: '0.95rem',
    fontWeight: 700,
    color: '#0f2d1f',
    margin: 0,
  },
  closeBtn: {
    background: 'none',
    border: 'none',
    fontSize: '1.1rem',
    cursor: 'pointer',
    color: '#666',
    padding: '2px 6px',
  },
  fieldGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '1rem',
  },
  field: {},
  fieldLabel: {
    display: 'block',
    fontSize: '0.75rem',
    fontWeight: 600,
    color: '#555',
    marginBottom: '4px',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.3px',
  },
  fieldInput: {
    width: '100%',
    padding: '0.5rem 0.7rem',
    border: '1px solid #ccc',
    borderRadius: '5px',
    fontSize: '0.85rem',
    boxSizing: 'border-box' as const,
  },
  toggleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    marginTop: '4px',
  },
  toggle: {
    width: '36px',
    height: '20px',
    borderRadius: '10px',
    border: 'none',
    cursor: 'pointer',
    position: 'relative' as const,
    transition: 'background 0.2s',
  },
  toggleOn: {
    backgroundColor: '#1a6b3c',
  },
  toggleOff: {
    backgroundColor: '#ccc',
  },
  toggleKnob: {
    position: 'absolute' as const,
    top: '2px',
    width: '16px',
    height: '16px',
    borderRadius: '50%',
    backgroundColor: '#fff',
    transition: 'left 0.2s',
  },
  toggleLabel: {
    fontSize: '0.8rem',
    color: '#333',
    fontWeight: 500,
  },
  btnRow: {
    display: 'flex',
    gap: '0.5rem',
    marginTop: '1rem',
  },
  saveBtn: {
    padding: '0.5rem 1.25rem',
    backgroundColor: '#1a6b3c',
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    fontSize: '0.85rem',
    fontWeight: 600,
    cursor: 'pointer',
  },
  saveBtnDisabled: {
    padding: '0.5rem 1.25rem',
    backgroundColor: '#a5d6a7',
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    fontSize: '0.85rem',
    fontWeight: 600,
    cursor: 'not-allowed',
  },
  cancelBtn: {
    padding: '0.5rem 1.25rem',
    backgroundColor: '#f5f5f5',
    color: '#333',
    border: '1px solid #e0e0e0',
    borderRadius: '6px',
    fontSize: '0.85rem',
    cursor: 'pointer',
  },
  toast: {
    position: 'fixed' as const,
    bottom: '24px',
    right: '24px',
    backgroundColor: '#2e7d32',
    color: '#fff',
    padding: '12px 20px',
    borderRadius: '8px',
    fontSize: '0.85rem',
    fontWeight: 500,
    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
    zIndex: 1100,
  },
  error: {
    color: '#d32f2f',
    backgroundColor: '#fdecea',
    padding: '0.5rem 0.75rem',
    borderRadius: '4px',
    marginBottom: '1rem',
    fontSize: '0.85rem',
  },
  loading: {
    textAlign: 'center' as const,
    padding: '2rem',
    color: '#666',
    fontSize: '0.9rem',
  },
  pagination: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '0.75rem',
    marginTop: '1rem',
  },
  pageBtn: {
    padding: '0.4rem 0.8rem',
    border: '1px solid #ccc',
    borderRadius: '4px',
    backgroundColor: '#fff',
    cursor: 'pointer',
    fontSize: '0.8rem',
  },
  pageBtnDisabled: {
    padding: '0.4rem 0.8rem',
    border: '1px solid #e0e0e0',
    borderRadius: '4px',
    backgroundColor: '#f5f5f5',
    cursor: 'not-allowed',
    fontSize: '0.8rem',
    color: '#aaa',
  },
  pageInfo: {
    fontSize: '0.8rem',
    color: '#666',
  },
};

export function GuestManagementView() {
  const [guests, setGuests] = useState<Guest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalGuests, setTotalGuests] = useState(0);

  const [selectedGuest, setSelectedGuest] = useState<Guest | null>(null);
  const [editForm, setEditForm] = useState({
    fullName: '',
    protocolVip: false,
    isChapterMember: false,
    familyAssociation: '',
    accessibilityNeeds: [] as string[],
    accessibilityOther: '',
    chapterAffiliation: '',
    dietaryPreference: '',
  });
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState('');
  const [chapters, setChapters] = useState<string[]>([]);
  const [mealOptions, setMealOptions] = useState<{ id: string; name: string }[]>([]);
  const [filterTier, setFilterTier] = useState('');
  const [filterChapter, setFilterChapter] = useState('');
  const [filterChapterMember, setFilterChapterMember] = useState('');
  const [sortBy, setSortBy] = useState('name');

  // Seat Family state
  const [seatFamilyModal, setSeatFamilyModal] = useState(false);
  const [seatFamilyLoading, setSeatFamilyLoading] = useState(false);
  const [seatFamilyResult, setSeatFamilyResult] = useState<{
    tableNumber: number;
    tableName: string;
    zone: string;
    seatedGuests: { id: string; fullName: string; ticketNumber: string | null; seatNumber: number; tier: string }[];
    skippedGuests: { id: string; fullName: string; reason: string }[];
    remainingSeats: number;
  } | null>(null);
  const [seatFamilyError, setSeatFamilyError] = useState('');

  // Multi-select for family group creation
  const [multiSelectMode, setMultiSelectMode] = useState(false);
  const [selectedGuestIds, setSelectedGuestIds] = useState<Set<string>>(new Set());
  const [selectedGuestDetails, setSelectedGuestDetails] = useState<Map<string, { id: string; fullName: string; tierName: string | null; ticketNumber: string | null; chapterAffiliation: string | null }>>(new Map());
  const [showCreateFamilyModal, setShowCreateFamilyModal] = useState(false);

  // Order-guests popup (opened by clicking the 👥 count badge)
  const [orderModal, setOrderModal] = useState<{ orderRef: string } | null>(null);
  const [orderModalGuests, setOrderModalGuests] = useState<Array<{ id: string; ticketNumber: number; tierName: string; status: string; guestName: string | null; meal: string | null }>>([]);
  const [orderModalLoading, setOrderModalLoading] = useState(false);

  const openOrderModal = async (orderId: string, orderRef: string) => {
    setOrderModal({ orderRef });
    setOrderModalLoading(true);
    setOrderModalGuests([]);
    try {
      const data = await apiClient<Array<{ id: string; ticketNumber: number; tierName: string; status: string; guestName: string | null; meal: string | null }>>(`/legacy-ball/orders/${orderId}/tickets`);
      setOrderModalGuests(Array.isArray(data) ? data : []);
    } catch {
      setOrderModalGuests([]);
    } finally {
      setOrderModalLoading(false);
    }
  };

  const fetchGuests = useCallback(async (searchTerm?: string, pageNum = 1, tierFilter?: string, chapterFilter?: string, sort?: string, chapterMemberFilter?: string) => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      if (searchTerm) params.set('query', searchTerm);
      if (tierFilter) params.set('tier', tierFilter);
      if (chapterFilter) params.set('chapter', chapterFilter);
      if (chapterMemberFilter) params.set('chapterMember', chapterMemberFilter);
      if (sort && sort !== 'name') params.set('sortBy', sort);
      params.set('page', String(pageNum));
      const queryStr = params.toString();
      const data = await apiClient<GuestsResponse>(`/table-management/guests?${queryStr}`);
      setGuests(data.guests || []);
      setPage(data.page);
      setTotalPages(data.totalPages);
      setTotalGuests(data.totalGuests);
    } catch (err: any) {
      setError(err?.message || 'Failed to load guests');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchGuests();
    // Fetch chapters for dropdown
    apiClient<{ id: string; name: string }[]>('/chapters')
      .then(data => setChapters(Array.isArray(data) ? data.map(c => c.name) : []))
      .catch(() => {});
    // Fetch meal options for dropdown
    apiClient<{ id: string; name: string }[]>('/legacy-ball/meal-options')
      .then(data => setMealOptions(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, [fetchGuests]);

  // Debounced search + filter changes
  useEffect(() => {
    const timer = setTimeout(() => {
      setSelectedGuest(null);
      fetchGuests(searchQuery || undefined, 1, filterTier || undefined, filterChapter || undefined, sortBy, filterChapterMember || undefined);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchQuery, filterTier, filterChapter, filterChapterMember, sortBy, fetchGuests]);

  const handleSelectGuest = (guest: Guest) => {
    if (selectedGuest?.id === guest.id) {
      setSelectedGuest(null);
      return;
    }
    setSelectedGuest(guest);
    // Parse accessibility needs - could be comma-separated string or single value
    const existingNeeds = guest.accessibilityNeeds || '';
    const needsList = existingNeeds.split(',').map(s => s.trim()).filter(Boolean);
    const knownNeeds = needsList.filter(n => ACCESSIBILITY_OPTIONS.includes(n));
    const otherNeeds = needsList.filter(n => !ACCESSIBILITY_OPTIONS.includes(n)).join(', ');
    setEditForm({
      fullName: guest.fullName,
      protocolVip: guest.protocolVip,
      isChapterMember: guest.isChapterMember || false,
      familyAssociation: guest.familyAssociation || '',
      accessibilityNeeds: knownNeeds,
      accessibilityOther: otherNeeds,
      chapterAffiliation: guest.chapterAffiliation || '',
      dietaryPreference: guest.dietaryPreference || '',
    });
  };

  const handleSave = async () => {
    if (!selectedGuest) return;
    setSaving(true);
    setError('');
    try {
      // Combine accessibility needs into a comma-separated string
      const allNeeds = [...editForm.accessibilityNeeds];
      if (editForm.accessibilityOther.trim()) {
        allNeeds.push(editForm.accessibilityOther.trim());
      }
      const accessibilityStr = allNeeds.join(', ') || null;

      // If name changed, use cascading rename endpoint
      const nameChanged = editForm.fullName.trim() !== selectedGuest.fullName;
      if (nameChanged) {
        await apiClient(`/table-management/guests/${selectedGuest.id}/rename`, {
          method: 'PATCH',
          body: { fullName: editForm.fullName.trim() },
        });
      }

      await apiClient(`/table-management/guests/${selectedGuest.id}`, {
        method: 'PATCH',
        body: {
          protocolVip: editForm.protocolVip,
          isChapterMember: editForm.isChapterMember,
          familyAssociation: editForm.familyAssociation || null,
          accessibilityNeeds: accessibilityStr,
          chapterAffiliation: editForm.chapterAffiliation || null,
          dietaryPreference: editForm.dietaryPreference || null,
        },
      });
      // Update local state
      setGuests((prev) =>
        prev.map((g) =>
          g.id === selectedGuest.id
            ? {
                ...g,
                fullName: nameChanged ? editForm.fullName.trim() : g.fullName,
                protocolVip: editForm.protocolVip,
                isChapterMember: editForm.isChapterMember,
                familyAssociation: editForm.familyAssociation || null,
                accessibilityNeeds: accessibilityStr,
                chapterAffiliation: editForm.chapterAffiliation || null,
                dietaryPreference: editForm.dietaryPreference || null,
              }
            : g,
        ),
      );
      setToast(`Updated ${editForm.fullName.trim()}`);
      setSelectedGuest(null);
      setTimeout(() => setToast(''), 3500);
    } catch (err: any) {
      setError(err?.message || 'Failed to save guest');
    } finally {
      setSaving(false);
    }
  };

  const handlePageChange = (newPage: number) => {
    setSelectedGuest(null);
    fetchGuests(searchQuery || undefined, newPage, filterTier || undefined, filterChapter || undefined, sortBy, filterChapterMember || undefined);
  };

  // Detect if current guests are all from the same family group
  const familyFilterValue = (() => {
    if (guests.length === 0) return null;
    const families = guests.map((g) => g.familyAssociation).filter(Boolean);
    if (families.length === 0) return null;
    const unique = [...new Set(families)];
    if (unique.length === 1 && families.length >= 2) return unique[0];
    return null;
  })();

  const handleSeatFamily = async () => {
    if (!familyFilterValue) return;
    setSeatFamilyLoading(true);
    setSeatFamilyError('');
    setSeatFamilyResult(null);
    try {
      const result = await apiClient<typeof seatFamilyResult>('/table-management/seat-family', {
        method: 'POST',
        body: { familyAssociation: familyFilterValue },
      });
      setSeatFamilyResult(result);
      setToast(`Family seated at Table ${result!.tableNumber}`);
      setTimeout(() => setToast(''), 4000);
      setSeatFamilyModal(false);
      fetchGuests(searchQuery || undefined, page);
    } catch (err: any) {
      setSeatFamilyError(err?.message || 'Failed to seat family');
    } finally {
      setSeatFamilyLoading(false);
    }
  };

  const handlePrintReport = async () => {
    // Fetch ALL guests (no pagination) for the report, respecting active filters and sort
    let allGuests: Guest[] = [];
    try {
      let pg = 1;
      let total = 1;
      while (pg <= total) {
        const params = new URLSearchParams();
        if (searchQuery) params.set('query', searchQuery);
        if (filterTier) params.set('tier', filterTier);
        if (filterChapter) params.set('chapter', filterChapter);
        if (filterChapterMember) params.set('chapterMember', filterChapterMember);
        if (sortBy && sortBy !== 'name') params.set('sortBy', sortBy);
        params.set('page', String(pg));
        const data = await apiClient<GuestsResponse>(`/table-management/guests?${params.toString()}`);
        allGuests = allGuests.concat(data.guests || []);
        total = data.totalPages;
        pg++;
      }
    } catch {
      allGuests = guests; // fallback to current page
    }

    // Build subtitle describing active filters
    const filterParts: string[] = [];
    if (searchQuery) filterParts.push(`Search: "${searchQuery}"`);
    if (filterTier) filterParts.push(`Tier: ${filterTier}`);
    if (filterChapter) filterParts.push(`Chapter: ${filterChapter}`);
    if (filterChapterMember === 'yes') filterParts.push('Chapter Members Only');
    if (filterChapterMember === 'no') filterParts.push('Non-Chapter Members');
    if (filterChapterMember === 'presidents') filterParts.push('Chapter Presidents');
    if (sortBy && sortBy !== 'name') filterParts.push(`Sorted by: ${sortBy}`);
    const filterLabel = filterParts.length > 0 ? ` • Filters: ${filterParts.join(', ')}` : '';

    const headerCells = ['#', 'Name', 'Chapter', 'Ticket', 'Tier', 'Order', 'Meal', 'Table', 'Seat', 'VIP'].map(h => `<th>${h}</th>`).join('');
    const colCount = 10;
    const useGroupSeparator = sortBy === 'order' || sortBy === 'chapter';
    let lastGroupValue = '';
    const rows = allGuests.map((guest, idx) => {
      const assignedSeat = guest.seat?.find(s => s.status === 'Assigned' || s.status === 'CheckedIn');
      const ticketNum = guest.ticketNumber || '';
      const tier = (guest as any).registration?.ticket?.tierName || '';
      const orderRef = (guest as any).registration?.ticket?.order?.referenceId || '';
      const tableNum = assignedSeat?.table?.tableNumber ?? '';
      const seatNum = assignedSeat?.seatNumber ?? '';
      const meal = guest.dietaryPreference || '';

      let separator = '';
      if (useGroupSeparator) {
        const groupValue = sortBy === 'order' ? (orderRef || '(no order)') : (guest.chapterAffiliation || '(no chapter)');
        if (idx > 0 && groupValue !== lastGroupValue) {
          separator = `<tr class="group-sep"><td colspan="${colCount}" style="border-top:3px double #333;border-bottom:none;padding:6px 10px;font-weight:700;font-size:11px;color:#333;background:#f5f5f5;">${sortBy === 'order' ? 'Order' : 'Chapter'}: ${groupValue}</td></tr>`;
        } else if (idx === 0) {
          separator = `<tr class="group-sep"><td colspan="${colCount}" style="border-bottom:none;padding:6px 10px;font-weight:700;font-size:11px;color:#333;background:#f5f5f5;">${sortBy === 'order' ? 'Order' : 'Chapter'}: ${groupValue}</td></tr>`;
        }
        lastGroupValue = groupValue;
      }

      return `${separator}<tr><td>${idx + 1}</td><td>${guest.fullName}</td><td>${guest.chapterAffiliation || ''}</td><td>${ticketNum}</td><td>${tier}</td><td>${orderRef}</td><td>${meal}</td><td>${tableNum}</td><td>${seatNum}</td><td>${guest.protocolVip ? 'VIP' : ''}</td></tr>`;
    }).join('');
    const html = `<!DOCTYPE html><html><head><title>Legacy Ball Guest Listing</title><style>body{font-family:sans-serif;padding:20px;}table{width:100%;border-collapse:collapse;}th{text-align:left;padding:8px 10px;border-bottom:2px solid #333;font-size:13px;}td{padding:6px 10px;border-bottom:1px solid #ddd;font-size:12px;}h1{font-size:18px;margin-bottom:4px;}p{color:#666;font-size:12px;margin-bottom:16px;}</style></head><body><h1>Legacy Ball — Guest Listing</h1><p>${allGuests.length} guests${filterLabel} • Printed ${new Date().toLocaleDateString()}</p><table><thead><tr>${headerCells}</tr></thead><tbody>${rows}</tbody></table></body></html>`;
    const printWindow = window.open('', '_blank');
    if (printWindow) { printWindow.document.write(html); printWindow.document.close(); printWindow.focus(); setTimeout(() => printWindow.print(), 300); }
  };

  // Multi-select handlers
  const handleToggleMultiSelect = () => {
    const entering = !multiSelectMode;
    setMultiSelectMode(entering);
    setSelectedGuestIds(new Set());
    setSelectedGuestDetails(new Map());
    setSelectedGuest(null);
    // Refresh guest list when entering multi-select to get fresh familyAssociation data
    if (entering) {
      fetchGuests(searchQuery || undefined, page, filterTier || undefined, filterChapter || undefined, sortBy);
    }
  };

  const handleToggleGuestSelection = (guestId: string) => {
    setSelectedGuestIds((prev) => {
      const next = new Set(prev);
      if (next.has(guestId)) {
        next.delete(guestId);
      } else {
        next.add(guestId);
      }
      return next;
    });
    // Store/remove guest details for cross-page persistence
    setSelectedGuestDetails((prev) => {
      const next = new Map(prev);
      if (next.has(guestId)) {
        next.delete(guestId);
      } else {
        const g = guests.find((guest) => guest.id === guestId);
        if (g) {
          next.set(guestId, {
            id: g.id,
            fullName: g.fullName,
            tierName: (g as any).registration?.ticket?.tierName || null,
            ticketNumber: g.ticketNumber || null,
            chapterAffiliation: g.chapterAffiliation || null,
          });
        }
      }
      return next;
    });
  };

  const handleSelectAll = () => {
    const eligible = guests.filter((g) => !g.familyAssociation && !(g.seat && g.seat.length > 0));
    if (selectedGuestIds.size === eligible.length) {
      // Deselect all on current page
      setSelectedGuestIds((prev) => {
        const next = new Set(prev);
        eligible.forEach((g) => next.delete(g.id));
        return next;
      });
      setSelectedGuestDetails((prev) => {
        const next = new Map(prev);
        eligible.forEach((g) => next.delete(g.id));
        return next;
      });
    } else {
      // Select all on current page
      setSelectedGuestIds((prev) => {
        const next = new Set(prev);
        eligible.forEach((g) => next.add(g.id));
        return next;
      });
      setSelectedGuestDetails((prev) => {
        const next = new Map(prev);
        eligible.forEach((g) => {
          if (!next.has(g.id)) {
            next.set(g.id, {
              id: g.id,
              fullName: g.fullName,
              tierName: (g as any).registration?.ticket?.tierName || null,
              ticketNumber: g.ticketNumber || null,
              chapterAffiliation: g.chapterAffiliation || null,
            });
          }
        });
        return next;
      });
    }
  };

  const handleOpenCreateFamily = () => {
    if (selectedGuestIds.size < 2) return;
    setShowCreateFamilyModal(true);
  };

  const handleFamilyGroupCreated = (groupName: string) => {
    setShowCreateFamilyModal(false);
    setMultiSelectMode(false);
    setSelectedGuestIds(new Set());
    setSelectedGuestDetails(new Map());
    setToast(`Family group "${groupName}" created`);
    setTimeout(() => setToast(''), 4000);
    fetchGuests(searchQuery || undefined, page, filterTier || undefined, filterChapter || undefined, sortBy);
  };

  const selectedGuestsForModal = Array.from(selectedGuestDetails.values());

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h3 style={styles.title}>Guest Attributes</h3>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <button
            style={{
              background: multiSelectMode ? '#1a6b3c' : 'none',
              color: multiSelectMode ? '#fff' : '#1a6b3c',
              border: '1px solid #1a6b3c',
              borderRadius: '6px',
              padding: '6px 12px',
              fontSize: '0.8rem',
              fontWeight: 600,
              cursor: 'pointer',
              transition: 'all 0.15s',
            }}
            onClick={handleToggleMultiSelect}
            aria-label={multiSelectMode ? 'Exit selection mode' : 'Select guests to create family group'}
            aria-pressed={multiSelectMode}
          >
            {multiSelectMode ? '✕ Cancel Selection' : '👨‍👩‍👧‍👦 Create Family Group'}
          </button>
          <button
            style={{ background: 'none', border: '1px solid #ccc', borderRadius: '6px', padding: '6px 12px', fontSize: '0.8rem', cursor: 'pointer', color: '#333' }}
            onClick={handlePrintReport}
            aria-label="Print guest listing report"
          >
            🖨️ Print
          </button>
          <span style={styles.countBadge}>{totalGuests} guests</span>
        </div>
      </div>

      {error && <div style={styles.error}>{error}</div>}

      <div style={styles.searchRow}>
        <input
          style={styles.searchInput}
          type="text"
          placeholder="Search by name, ticket #, chapter, table, or order ID..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          aria-label="Search guests"
        />
        <select
          style={{ padding: '0.45rem 0.6rem', fontSize: '0.82rem', border: '1px solid #ccc', borderRadius: '4px', minWidth: '120px' }}
          value={filterTier}
          onChange={(e) => setFilterTier(e.target.value)}
          aria-label="Filter by tier"
        >
          <option value="">All Tiers</option>
          <option value="Patron">Patron</option>
          <option value="General">General</option>
        </select>
        <select
          style={{ padding: '0.45rem 0.6rem', fontSize: '0.82rem', border: '1px solid #ccc', borderRadius: '4px', minWidth: '130px' }}
          value={filterChapter}
          onChange={(e) => setFilterChapter(e.target.value)}
          aria-label="Filter by chapter"
        >
          <option value="">All Chapters</option>
          <option value="__unaffiliated__">Unaffiliated (no chapter)</option>
          {chapters.map((ch) => (
            <option key={ch} value={ch}>{ch}</option>
          ))}
        </select>
        <select
          style={{ padding: '0.45rem 0.6rem', fontSize: '0.82rem', border: '1px solid #ccc', borderRadius: '4px', minWidth: '130px' }}
          value={filterChapterMember}
          onChange={(e) => setFilterChapterMember(e.target.value)}
          aria-label="Filter by chapter membership"
        >
          <option value="">All Guests</option>
          <option value="yes">GA Chapter Members</option>
          <option value="no">Non-Members</option>
          <option value="presidents">Chapter Presidents</option>
        </select>
        <select
          style={{ padding: '0.45rem 0.6rem', fontSize: '0.82rem', border: '1px solid #ccc', borderRadius: '4px', minWidth: '110px' }}
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
          aria-label="Sort by"
        >
          <option value="name">Sort: Name</option>
          <option value="order">Sort: Order ID</option>
          <option value="chapter">Sort: Chapter</option>
          <option value="ticket">Sort: Ticket #</option>
        </select>
      </div>

      {/* Multi-select toolbar */}
      {multiSelectMode && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            padding: '8px 14px',
            backgroundColor: '#e8f5e9',
            borderRadius: '6px',
            marginBottom: '0.75rem',
            border: '1px solid #c8e6c9',
          }}
        >
          <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.82rem', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={guests.filter((g) => !g.familyAssociation && !(g.seat && g.seat.length > 0)).length > 0 && selectedGuestIds.size === guests.filter((g) => !g.familyAssociation && !(g.seat && g.seat.length > 0)).length}
              onChange={handleSelectAll}
              style={{ accentColor: '#1a6b3c' }}
              aria-label="Select all eligible guests on this page"
            />
            Select All
          </label>
          <span style={{ fontSize: '0.82rem', color: '#333', fontWeight: 500 }}>
            {selectedGuestIds.size} selected
          </span>
          <div style={{ flex: 1 }} />
          <button
            style={{
              padding: '5px 14px',
              backgroundColor: selectedGuestIds.size >= 2 ? '#1a6b3c' : '#a5d6a7',
              color: '#fff',
              border: 'none',
              borderRadius: '5px',
              fontSize: '0.8rem',
              fontWeight: 600,
              cursor: selectedGuestIds.size >= 2 ? 'pointer' : 'not-allowed',
            }}
            onClick={handleOpenCreateFamily}
            disabled={selectedGuestIds.size < 2}
            aria-label="Create family group from selected guests"
          >
            Create Group ({selectedGuestIds.size})
          </button>
        </div>
      )}

      {/* Eligibility rules info (visible in multi-select mode) */}
      {multiSelectMode && (
        <div style={{ marginBottom: '0.75rem', padding: '10px 14px', backgroundColor: '#fffde7', border: '1px solid #fff9c4', borderRadius: '6px', fontSize: '0.78rem', color: '#5d4037' }}>
          <strong style={{ fontSize: '0.8rem' }}>Eligibility Rules — Guests with disabled checkboxes cannot be added:</strong>
          <table style={{ marginTop: '6px', borderCollapse: 'collapse', width: '100%', fontSize: '0.76rem' }}>
            <tbody>
              <tr><td style={{ padding: '3px 8px', fontWeight: 600, width: '20px' }}>1.</td><td style={{ padding: '3px 0' }}>Guest must NOT already be in a family group</td></tr>
              <tr><td style={{ padding: '3px 8px', fontWeight: 600 }}>2.</td><td style={{ padding: '3px 0' }}>Guest must NOT already have a seat assignment (Assigned or Checked In)</td></tr>
              <tr><td style={{ padding: '3px 8px', fontWeight: 600 }}>3.</td><td style={{ padding: '3px 0' }}>Minimum 2 eligible guests must be selected to create a group</td></tr>
            </tbody>
          </table>
        </div>
      )}

      {loading ? (
        <div style={styles.loading}>Loading guests...</div>
      ) : guests.length === 0 ? (
        <div style={styles.loading}>No guests found</div>
      ) : (
        <>
          <table style={styles.table}>
            <thead>
              <tr>
                {multiSelectMode && (
                  <th style={{ ...styles.th, width: '36px', textAlign: 'center', padding: '0.6rem 4px' }}></th>
                )}
                <th style={{ ...styles.th, width: '28px', textAlign: 'center', padding: '0.6rem 4px' }}>♿</th>
                <th style={styles.th}>Name</th>
                <th style={styles.th}>Chapter</th>
                <th style={styles.th}>Ticket #</th>
                <th style={styles.th}>Tier</th>
                <th style={styles.th}>Order</th>
                <th style={styles.th}>Meal</th>
                <th style={styles.th}>Table</th>
                <th style={styles.th}>Seat</th>
                <th style={styles.th}>VIP</th>
              </tr>
            </thead>
            <tbody>
              {(() => {
                // Compute order grouping for visual banding when sorted by order
                let lastOrderRef = '';
                let bandIndex = 0;
                const isOrderSort = sortBy === 'order';

                return guests.map((guest, gIdx) => {
                const assignedSeat = guest.seat?.find(s => s.status === 'Assigned' || s.status === 'CheckedIn');
                const ticketNum = guest.ticketNumber || '—';
                const tier = (guest as any).registration?.ticket?.tierName || '—';
                const orderRef = (guest as any).registration?.ticket?.order?.referenceId || '—';
                const tableNum = assignedSeat?.table?.tableNumber ?? '—';
                const seatNum = assignedSeat?.seatNumber ?? '—';
                const isInFamily = !!guest.familyAssociation;
                const isSeated = !!assignedSeat;
                const isDisabled = isInFamily || isSeated;
                const isSelected = selectedGuestIds.has(guest.id);

                // Order group tracking
                const orderChanged = isOrderSort && orderRef !== lastOrderRef && orderRef !== '—';
                if (orderRef !== lastOrderRef) { bandIndex++; lastOrderRef = orderRef; }
                const bandBg = isOrderSort && orderRef !== '—' ? (bandIndex % 2 === 0 ? '#f0fdf4' : '#ffffff') : undefined;

                // Count guests in this order for the separator
                const orderGuestCount = isOrderSort && orderChanged
                  ? guests.filter(g => ((g as any).registration?.ticket?.order?.referenceId || '—') === orderRef).length
                  : 0;

                const colSpan = multiSelectMode ? 12 : 11;

                return (
                  <React.Fragment key={guest.id}>
                    {orderChanged && (
                      <tr>
                        <td colSpan={colSpan} style={{ padding: '4px 10px', fontSize: '0.72rem', fontWeight: 700, color: '#1a6b3c', backgroundColor: '#e8f5e9', borderTop: '2px solid #a5d6a7', borderBottom: '1px solid #c8e6c9' }}>
                          📦 Order {orderRef} ({orderGuestCount} guest{orderGuestCount !== 1 ? 's' : ''})
                        </td>
                      </tr>
                    )}
                  <tr
                    key={guest.id}
                    style={selectedGuest?.id === guest.id ? styles.rowSelected : (multiSelectMode && isSelected ? { ...styles.row, backgroundColor: '#f1f8f4' } : { ...styles.row, ...(bandBg ? { backgroundColor: bandBg } : {}) })}
                    onClick={() => {
                      if (multiSelectMode) {
                        if (!isDisabled) handleToggleGuestSelection(guest.id);
                      } else {
                        handleSelectGuest(guest);
                      }
                    }}
                    onMouseEnter={(e) => {
                      if (selectedGuest?.id !== guest.id && !(multiSelectMode && isSelected)) {
                        e.currentTarget.style.backgroundColor = '#f5f5f5';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (selectedGuest?.id !== guest.id && !(multiSelectMode && isSelected)) {
                        e.currentTarget.style.backgroundColor = '';
                      }
                    }}
                  >
                    {multiSelectMode && (
                      <td style={{ ...styles.td, width: '36px', textAlign: 'center', padding: '0.6rem 4px' }}>
                        <input
                          type="checkbox"
                          checked={isSelected}
                          disabled={isDisabled}
                          onChange={() => { if (!isDisabled) handleToggleGuestSelection(guest.id); }}
                          onClick={(e) => e.stopPropagation()}
                          title={isInFamily ? `Already in group: ${guest.familyAssociation}` : isSeated ? 'Already seated — cannot add to family group' : 'Select for family group'}
                          aria-label={isDisabled ? `${guest.fullName} not eligible` : `Select ${guest.fullName}`}
                          style={{ accentColor: '#1a6b3c', cursor: isDisabled ? 'not-allowed' : 'pointer', opacity: isDisabled ? 0.4 : 1 }}
                        />
                      </td>
                    )}
                    <td style={{ ...styles.td, width: '28px', textAlign: 'center', padding: '0.6rem 4px' }}>
                      {guest.accessibilityNeeds ? <span title={guest.accessibilityNeeds} style={{ fontSize: '0.9rem' }}>♿</span> : ''}
                    </td>
                    <td style={styles.td}>
                      {guest.fullName}
                      {guest.familyAssociation?.toUpperCase().includes('RES') && (
                        <span style={{ marginLeft: '5px', color: '#d32f2f', fontWeight: 700, fontSize: '0.75rem' }} title={`RES group: ${guest.familyAssociation}`}>&raquo;</span>
                      )}
                      {multiSelectMode && isInFamily && (
                        <span style={{ marginLeft: '6px', fontSize: '0.68rem', backgroundColor: '#e3f2fd', color: '#1565c0', padding: '1px 6px', borderRadius: '8px', fontWeight: 500 }}>
                          {guest.familyAssociation}
                        </span>
                      )}
                    </td>
                    <td style={{ ...styles.td, fontSize: '0.8rem', color: guest.chapterAffiliation ? '#333' : '#bbb' }}>{guest.chapterAffiliation || '—'}</td>
                    <td style={{ ...styles.td, fontFamily: 'monospace', fontSize: '0.8rem' }}>{ticketNum}</td>
                    <td style={{ ...styles.td, fontSize: '0.8rem' }}>{tier}</td>
                    <td style={{ ...styles.td, fontFamily: 'monospace', fontSize: '0.75rem', color: orderRef !== '—' ? '#1565c0' : '#bbb' }}>
                      {orderRef}
                      {guest.orderGuestCount != null && guest.orderGuestCount >= 2 && guest.registration?.ticket?.orderId && (
                        <button
                          type="button"
                          title={`View all ${guest.orderGuestCount} guests in this order`}
                          aria-label={`View all ${guest.orderGuestCount} guests in order ${orderRef}`}
                          onClick={(e) => { e.stopPropagation(); openOrderModal(guest.registration!.ticket!.orderId!, orderRef); }}
                          style={{ marginLeft: '5px', display: 'inline-flex', alignItems: 'center', gap: '2px', fontFamily: 'inherit', fontSize: '0.68rem', fontWeight: 700, color: '#1565c0', backgroundColor: '#e3f2fd', border: 'none', borderRadius: '8px', padding: '1px 6px', cursor: 'pointer' }}
                        >
                          👥 {guest.orderGuestCount}
                        </button>
                      )}
                    </td>
                    <td style={{ ...styles.td, fontSize: '0.8rem', color: guest.dietaryPreference ? '#333' : '#bbb' }}>{guest.dietaryPreference || '—'}</td>
                    <td style={{ ...styles.td, textAlign: 'center' }}>{tableNum}</td>
                    <td style={{ ...styles.td, textAlign: 'center' }}>{seatNum}</td>
                    <td style={styles.td}>
                      {guest.protocolVip && <span style={styles.vipBadge}>VIP</span>}
                      {guest.isChapterMember && <span style={styles.chapterMemberBadge}>GA</span>}
                    </td>
                  </tr>
                  </React.Fragment>
                );
              });
              })()}
            </tbody>
          </table>

          {/* Pagination */}
          {totalPages > 1 && (
            <div style={styles.pagination}>
              <button
                style={page <= 1 ? styles.pageBtnDisabled : styles.pageBtn}
                disabled={page <= 1}
                onClick={() => handlePageChange(page - 1)}
              >
                ← Prev
              </button>
              <span style={styles.pageInfo}>
                Page {page} of {totalPages}
              </span>
              <button
                style={page >= totalPages ? styles.pageBtnDisabled : styles.pageBtn}
                disabled={page >= totalPages}
                onClick={() => handlePageChange(page + 1)}
              >
                Next →
              </button>
            </div>
          )}
        </>
      )}

      {/* Guest Detail & Edit Modal */}
      {selectedGuest && (() => {
        const assignedSeat = selectedGuest.seat?.find(s => s.status === 'Assigned' || s.status === 'CheckedIn');
        const ticketNum = selectedGuest.ticketNumber || '—';
        const tier = (selectedGuest as any).registration?.ticket?.tierName || '—';
        const orderRef = (selectedGuest as any).registration?.ticket?.order?.referenceId || '—';
        const tableNum = assignedSeat?.table?.tableNumber ?? '—';
        const seatNum = assignedSeat?.seatNumber ?? '—';
        const zone = assignedSeat?.table?.zone?.name || '—';

        return (
          <div style={styles.modalOverlay} onClick={() => setSelectedGuest(null)}>
            <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
              <div style={styles.editHeader}>
                <h4 style={styles.editTitle}>{selectedGuest.fullName}</h4>
                <button style={styles.closeBtn} onClick={() => setSelectedGuest(null)} aria-label="Close">✕</button>
              </div>

              {/* Read-Only Info Section */}
              <div style={{ backgroundColor: '#f8faf9', borderRadius: '8px', padding: '12px 16px', marginBottom: '16px', border: '1px solid #e2e8e4' }}>
                <div style={{ fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: '#5c6b63', marginBottom: '8px' }}>Guest Information</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px' }}>
                  <div>
                    <div style={{ fontSize: '0.7rem', color: '#888', marginBottom: '2px' }}>Ticket #</div>
                    <div style={{ fontSize: '0.85rem', fontFamily: 'monospace', fontWeight: 600 }}>{ticketNum}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: '0.7rem', color: '#888', marginBottom: '2px' }}>Tier</div>
                    <div style={{ fontSize: '0.85rem', fontWeight: 500 }}>{tier}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: '0.7rem', color: '#888', marginBottom: '2px' }}>Order</div>
                    <div style={{ fontSize: '0.85rem', fontFamily: 'monospace', color: orderRef !== '—' ? '#1565c0' : '#999', display: 'flex', alignItems: 'center', gap: '5px' }}>
                      {orderRef}
                      {selectedGuest.orderGuestCount != null && selectedGuest.orderGuestCount >= 2 && selectedGuest.registration?.ticket?.orderId && (
                        <button
                          type="button"
                          title={`View all ${selectedGuest.orderGuestCount} guests in this order`}
                          aria-label={`View all ${selectedGuest.orderGuestCount} guests in order ${orderRef}`}
                          onClick={() => openOrderModal(selectedGuest.registration!.ticket!.orderId!, orderRef)}
                          style={{ fontSize: '0.68rem', fontWeight: 700, color: '#1565c0', backgroundColor: '#e3f2fd', border: 'none', borderRadius: '8px', padding: '1px 6px', cursor: 'pointer' }}
                        >👥 {selectedGuest.orderGuestCount}</button>
                      )}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: '0.7rem', color: '#888', marginBottom: '2px' }}>Table</div>
                    <div style={{ fontSize: '0.85rem', fontWeight: 500 }}>{tableNum}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: '0.7rem', color: '#888', marginBottom: '2px' }}>Seat</div>
                    <div style={{ fontSize: '0.85rem', fontWeight: 500 }}>{seatNum}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: '0.7rem', color: '#888', marginBottom: '2px' }}>Zone</div>
                    <div style={{ fontSize: '0.85rem', fontWeight: 500 }}>{zone}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: '0.7rem', color: '#888', marginBottom: '2px' }}>RSVP</div>
                    <div style={{ fontSize: '0.85rem' }}>{selectedGuest.rsvpStatus || '—'}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: '0.7rem', color: '#888', marginBottom: '2px' }}>Payment</div>
                    <div style={{ fontSize: '0.85rem' }}>{selectedGuest.paymentStatus || '—'}</div>
                  </div>
                </div>
              </div>

              {/* Delete Ticket Action */}
              {selectedGuest.ticketNumber && selectedGuest.ticketNumber !== '—' && (
                <TicketDeleteFromGuest ticketNumber={selectedGuest.ticketNumber} guestId={selectedGuest.id} onDeleted={() => { setSelectedGuest(null); fetchGuests(searchQuery || undefined, page, filterTier || undefined, filterChapter || undefined, sortBy, filterChapterMember || undefined); }} />
              )}

              {/* Editable Fields Section */}
              <div style={{ fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: '#1a6b3c', marginBottom: '10px' }}>Edit Attributes</div>

              {/* Full Name — cascading rename */}
              <div style={{ marginBottom: '14px' }}>
                <label style={styles.fieldLabel}>Full Name (cascades to Ticket &amp; Account)</label>
                <input
                  type="text"
                  style={styles.fieldInput}
                  value={editForm.fullName}
                  onChange={(e) => setEditForm((f) => ({ ...f, fullName: e.target.value }))}
                  placeholder="Guest full name"
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', marginBottom: '16px' }}>
                {/* VIP Toggle */}
                <div>
                  <span style={styles.fieldLabel}>Protocol VIP</span>
                  <div style={styles.toggleRow}>
                    <button
                      type="button"
                      style={{ ...styles.toggle, ...(editForm.protocolVip ? styles.toggleOn : styles.toggleOff) }}
                      onClick={() => setEditForm((f) => ({ ...f, protocolVip: !f.protocolVip }))}
                      aria-label={`VIP status: ${editForm.protocolVip ? 'On' : 'Off'}`}
                      aria-pressed={editForm.protocolVip}
                    >
                      <span style={{ ...styles.toggleKnob, left: editForm.protocolVip ? '18px' : '2px' }} />
                    </button>
                    <span style={styles.toggleLabel}>{editForm.protocolVip ? 'Yes' : 'No'}</span>
                  </div>
                </div>

                {/* Chapter Member Toggle */}
                <div>
                  <span style={styles.fieldLabel}>Chapter Member</span>
                  <div style={styles.toggleRow}>
                    <button
                      type="button"
                      style={{ ...styles.toggle, ...(editForm.isChapterMember ? styles.toggleOn : styles.toggleOff) }}
                      onClick={() => setEditForm((f) => ({ ...f, isChapterMember: !f.isChapterMember }))}
                      aria-label={`Chapter member: ${editForm.isChapterMember ? 'On' : 'Off'}`}
                      aria-pressed={editForm.isChapterMember}
                    >
                      <span style={{ ...styles.toggleKnob, left: editForm.isChapterMember ? '18px' : '2px' }} />
                    </button>
                    <span style={styles.toggleLabel}>{editForm.isChapterMember ? 'Yes' : 'No'}</span>
                  </div>
                </div>

                {/* Chapter Affiliation */}
                <div>
                  <label style={styles.fieldLabel}>Chapter Affiliation</label>
                  <select
                    style={styles.fieldInput}
                    value={chapters.includes(editForm.chapterAffiliation) ? editForm.chapterAffiliation : (editForm.chapterAffiliation ? '__other__' : '')}
                    onChange={(e) => {
                      if (e.target.value === '__other__') {
                        setEditForm((f) => ({ ...f, chapterAffiliation: '' }));
                      } else {
                        setEditForm((f) => ({ ...f, chapterAffiliation: e.target.value }));
                      }
                    }}
                  >
                    <option value="">— Select Chapter —</option>
                    {chapters.map(ch => (<option key={ch} value={ch}>{ch}</option>))}
                    <option value="__other__">Other</option>
                  </select>
                  {(!chapters.includes(editForm.chapterAffiliation) && editForm.chapterAffiliation !== '') && (
                    <input style={{ ...styles.fieldInput, marginTop: '6px' }} type="text" value={editForm.chapterAffiliation} onChange={(e) => setEditForm((f) => ({ ...f, chapterAffiliation: e.target.value }))} placeholder="Enter chapter name" />
                  )}
                </div>

                {/* Family Association */}
                <div>
                  <label style={styles.fieldLabel}>Family Association</label>
                  <input style={styles.fieldInput} type="text" value={editForm.familyAssociation} onChange={(e) => setEditForm((f) => ({ ...f, familyAssociation: e.target.value }))} placeholder="e.g. Doe Family" />
                </div>

                {/* Meal / Dietary Preference — cascades to Seat + Registration */}
                <div>
                  <label style={styles.fieldLabel}>Meal Choice (cascades to Seat &amp; Registration)</label>
                  <select
                    style={styles.fieldInput}
                    value={mealOptions.some(m => m.name === editForm.dietaryPreference) ? editForm.dietaryPreference : (editForm.dietaryPreference ? '__other__' : '')}
                    onChange={(e) => {
                      if (e.target.value === '__other__') {
                        setEditForm((f) => ({ ...f, dietaryPreference: '' }));
                      } else {
                        setEditForm((f) => ({ ...f, dietaryPreference: e.target.value }));
                      }
                    }}
                  >
                    <option value="">— No Meal Selected —</option>
                    {mealOptions.map(opt => (<option key={opt.id} value={opt.name}>{opt.name}</option>))}
                    <option value="__other__">Other / Custom</option>
                  </select>
                  {(editForm.dietaryPreference && !mealOptions.some(m => m.name === editForm.dietaryPreference)) && (
                    <input style={{ ...styles.fieldInput, marginTop: '6px' }} type="text" value={editForm.dietaryPreference} onChange={(e) => setEditForm((f) => ({ ...f, dietaryPreference: e.target.value }))} placeholder="Enter dietary preference" />
                  )}
                </div>
              </div>

              {/* Accessibility Needs — Multi-Select Checkboxes */}
              <div style={{ marginBottom: '16px' }}>
                <label style={styles.fieldLabel}>Accessibility Needs (select all that apply)</label>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px', marginTop: '6px' }}>
                  {ACCESSIBILITY_OPTIONS.map(opt => (
                    <label key={opt} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.82rem', color: '#333', cursor: 'pointer', padding: '4px 0' }}>
                      <input
                        type="checkbox"
                        checked={editForm.accessibilityNeeds.includes(opt)}
                        onChange={(e) => {
                          setEditForm((f) => ({
                            ...f,
                            accessibilityNeeds: e.target.checked
                              ? [...f.accessibilityNeeds, opt]
                              : f.accessibilityNeeds.filter(n => n !== opt),
                          }));
                        }}
                        style={{ accentColor: '#1a6b3c' }}
                      />
                      {opt}
                    </label>
                  ))}
                </div>
                <input
                  style={{ ...styles.fieldInput, marginTop: '8px' }}
                  type="text"
                  value={editForm.accessibilityOther}
                  onChange={(e) => setEditForm((f) => ({ ...f, accessibilityOther: e.target.value }))}
                  placeholder="Other accessibility needs..."
                />
              </div>

              <div style={styles.btnRow}>
                <button style={saving ? styles.saveBtnDisabled : styles.saveBtn} onClick={handleSave} disabled={saving}>
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
                <button style={styles.cancelBtn} onClick={() => setSelectedGuest(null)}>Cancel</button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Success Toast */}
      {toast && (
        <div style={styles.toast} role="status" aria-live="polite">
          ✓ {toast}
        </div>
      )}

      {/* Create Family Group Modal */}
      {showCreateFamilyModal && (
        <CreateFamilyGroupModal
          guests={selectedGuestsForModal}
          onClose={() => setShowCreateFamilyModal(false)}
          onCreated={handleFamilyGroupCreated}
        />
      )}

      {/* Order Guests Popup — opened by clicking the 👥 count badge */}
      {orderModal && (
        <div style={styles.modalOverlay} onClick={() => setOrderModal(null)}>
          <div style={{ ...styles.modalContent, maxWidth: '460px' }} onClick={(e) => e.stopPropagation()}>
            <div style={styles.editHeader}>
              <h4 style={styles.editTitle}>👥 Guests in Order {orderModal.orderRef}</h4>
              <button style={styles.closeBtn} onClick={() => setOrderModal(null)} aria-label="Close">✕</button>
            </div>
            {orderModalLoading ? (
              <div style={{ padding: '1.5rem', textAlign: 'center', color: '#6b7280', fontSize: '0.85rem' }}>Loading…</div>
            ) : orderModalGuests.length === 0 ? (
              <div style={{ padding: '1.5rem', textAlign: 'center', color: '#6b7280', fontSize: '0.85rem' }}>No guests found for this order.</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem', marginTop: '0.5rem' }}>
                {orderModalGuests.map((g, i) => (
                  <div key={g.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.5rem', padding: '0.55rem 0.75rem', backgroundColor: '#f9fafb', border: '1px solid #eef0ee', borderRadius: '8px' }}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: '0.88rem', fontWeight: 600, color: '#0f2d1f' }}>{i + 1}. {g.guestName || 'Unassigned'}</div>
                      <div style={{ fontSize: '0.72rem', color: '#6b7280', fontFamily: 'monospace' }}>
                        TKT-{String(g.ticketNumber).padStart(3, '0')} · {g.tierName}{g.meal ? ` · ${g.meal}` : ''}
                      </div>
                    </div>
                    <span style={{ fontSize: '0.68rem', fontWeight: 600, padding: '2px 8px', borderRadius: '10px', backgroundColor: g.status === 'Active' ? '#dcfce7' : '#f3f4f6', color: g.status === 'Active' ? '#166534' : '#6b7280', whiteSpace: 'nowrap' }}>{g.status}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/* ─── Ticket Delete from Guest Panel ─── */
function TicketDeleteFromGuest({ ticketNumber, guestId, onDeleted }: { ticketNumber: string; guestId: string; onDeleted: () => void }) {
  const [impact, setImpact] = useState<any>(null);
  const [noTicket, setNoTicket] = useState(false);
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const handleCheck = async () => {
    setLoading(true);
    setNoTicket(false);
    try {
      const ticket = await apiClient<{ id: string }>(`/order-data-management/tickets/by-number/${encodeURIComponent(ticketNumber)}`);
      const result = await apiClient<any>(`/order-data-management/tickets/${ticket.id}/pre-delete-check`);
      setImpact(result);
    } catch {
      // No ticket record exists — offer to delete the guest record directly
      setNoTicket(true);
    }
    setLoading(false);
  };

  const handleDeleteTicket = async () => {
    if (!impact) return;
    setDeleting(true);
    try {
      await apiClient(`/order-data-management/tickets/${impact.ticketId}`, { method: 'DELETE' });
      setImpact(null);
      onDeleted();
    } catch (e: any) {
      alert(e?.message || 'Delete failed');
    }
    setDeleting(false);
  };

  const handleDeleteGuest = async () => {
    setDeleting(true);
    try {
      await apiClient(`/order-data-management/guest-records/${guestId}`, { method: 'DELETE' });
      onDeleted();
    } catch (e: any) {
      alert(e?.message || 'Delete failed');
    }
    setDeleting(false);
  };

  // Ticket impact view
  if (impact) {
    return (
      <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', padding: '12px', marginBottom: '16px' }}>
        <div style={{ fontWeight: 700, fontSize: '0.82rem', color: '#991b1b', marginBottom: '8px' }}>🗑️ Delete {impact.ticketNumber} — Impact</div>
        {impact.revenueImpact > 0 && (
          <div style={{ fontSize: '0.78rem', color: '#92400e', marginBottom: '6px' }}>Revenue impact: -${impact.revenueImpact.toFixed(2)}</div>
        )}
        {impact.dependencies.length > 0 && (
          <div style={{ fontSize: '0.75rem', marginBottom: '8px' }}>
            {impact.dependencies.map((d: any, i: number) => (
              <div key={i} style={{ marginBottom: '3px' }}><strong style={{ color: '#dc2626' }}>{d.type}:</strong> {d.detail}</div>
            ))}
          </div>
        )}
        <div style={{ display: 'flex', gap: '6px' }}>
          <button onClick={handleDeleteTicket} disabled={deleting} style={{ padding: '5px 12px', fontSize: '0.78rem', fontWeight: 600, border: 'none', borderRadius: '5px', backgroundColor: '#dc2626', color: '#fff', cursor: 'pointer', opacity: deleting ? 0.6 : 1 }}>{deleting ? 'Deleting...' : 'Confirm Delete'}</button>
          <button onClick={() => setImpact(null)} style={{ padding: '5px 12px', fontSize: '0.78rem', fontWeight: 600, border: '1px solid #d1d5db', borderRadius: '5px', backgroundColor: '#f9fafb', color: '#374151', cursor: 'pointer' }}>Cancel</button>
        </div>
      </div>
    );
  }

  // No ticket found — offer guest record deletion
  if (noTicket) {
    return (
      <div style={{ background: '#fef3c7', border: '1px solid #fbbf24', borderRadius: '8px', padding: '12px', marginBottom: '16px' }}>
        <div style={{ fontWeight: 700, fontSize: '0.82rem', color: '#92400e', marginBottom: '6px' }}>⚠️ No Ticket Record Found</div>
        <div style={{ fontSize: '0.78rem', color: '#78350f', marginBottom: '8px', lineHeight: 1.5 }}>
          This guest has ticket number <strong>{ticketNumber}</strong> on their record, but no matching Ticket exists in the database. This is orphaned/inconsistent data.
          <br />You can delete the <strong>Guest Record</strong> directly to clean this up.
        </div>
        <div style={{ display: 'flex', gap: '6px' }}>
          <button onClick={handleDeleteGuest} disabled={deleting} style={{ padding: '5px 12px', fontSize: '0.78rem', fontWeight: 600, border: 'none', borderRadius: '5px', backgroundColor: '#dc2626', color: '#fff', cursor: 'pointer', opacity: deleting ? 0.6 : 1 }}>{deleting ? 'Deleting...' : 'Delete Guest Record'}</button>
          <button onClick={() => setNoTicket(false)} style={{ padding: '5px 12px', fontSize: '0.78rem', fontWeight: 600, border: '1px solid #d1d5db', borderRadius: '5px', backgroundColor: '#f9fafb', color: '#374151', cursor: 'pointer' }}>Cancel</button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ marginBottom: '16px' }}>
      <button onClick={handleCheck} disabled={loading} style={{ padding: '5px 12px', fontSize: '0.78rem', fontWeight: 600, border: '1px solid #fecaca', borderRadius: '5px', backgroundColor: '#fef2f2', color: '#dc2626', cursor: 'pointer', opacity: loading ? 0.6 : 1 }}>
        {loading ? '⏳ Checking...' : '🗑️ Delete Ticket'}
      </button>
    </div>
  );
}
